import React, { FC, useContext, useEffect } from 'react';
import { UUID } from '../../types/uuid';
import Link from '@mui/material/Link';
import { GuardedRouteLink } from '../../resources/GuardedRouteLink';
import { keyframes, styled } from '@mui/material';
import { LazyOrganizationContext, TLazyOrganization } from '../../contexts/LazyOrganizationContext';
import { LazyFailed } from '../LazyFailed/LazyFailed';

interface IProps {
  id: UUID;
  Render?: FC<{ organization: TLazyOrganization }>;
}

const placeholderAnimation = keyframes({
  '0%': {
    backgroundPositionX: '0%',
  },
  '100%': {
    backgroundPositionX: '100px',
  },
});

const LazyPlaceholder = styled('span')({
  // background: '#ffffff20',
  backgroundSize: '50px',
  backgroundImage: 'linear-gradient(90deg, #ffffff20 0%, #ffffff2a 50%, #ffffff20 100%)',
  borderRadius: 4,
  color: 'transparent',
  animation: placeholderAnimation,
  animationDuration: '3s',
  animationIterationCount: 'infinite',
  animationTimingFunction: 'linear',
  '&:before': {
    content: '"...................."',
  },
});

const DefaultLazyOrganizationRenderer: FC<{ organization: TLazyOrganization }> = ({ organization }) => {
  return (
    <Link component={GuardedRouteLink} route={'OrganizationDetails'} params={[organization.name]} showFallback>
      {organization.name}
    </Link>
  );
};

export const LazyOrganization: FC<IProps> = ({ id, Render = DefaultLazyOrganizationRenderer }) => {
  const {
    data: organizations,
    requestData: requestOrganizationData,
    failedData: failedOrganizations,
  } = useContext(LazyOrganizationContext);

  const lcId = id?.toLowerCase();

  useEffect(() => {
    requestOrganizationData(lcId);
  }, [lcId, requestOrganizationData]);

  const organization = organizations[lcId];
  const failed = !organization && failedOrganizations.includes(lcId);

  return organization ? (
    <Render organization={organization} />
  ) : failed ? (
    <LazyFailed id={lcId} />
  ) : (
    <LazyPlaceholder />
  );
};
